import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Edit, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./ProductTemplates.css";

export default function ProductTemplates() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  
  const API = `${process.env.REACT_APP_API_BASE_URL}/product-templates`;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateName, setTemplateName] = useState("");

  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [fieldName, setFieldName] = useState("");
  const [fieldType, setFieldType] = useState("text");

  const [dropdownOptions, setDropdownOptions] = useState([""]);

  // Fetch templates
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await fetch(API, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load templates");

      setTemplates(data);
      setError("");
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Create or update
  const handleSubmitTemplate = async (e) => {
    e.preventDefault();
    setError("");

    const method = editingTemplate ? "PUT" : "POST";
    const url = editingTemplate ? `${API}/${editingTemplate.templateId}` : API;

    const body = JSON.stringify({ templateName: templateName });

    try {
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed");

      setShowForm(false);
      setEditingTemplate(null);
      setTemplateName("");
      fetchTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete template
  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;

    try {
      const res = await fetch(`${API}/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      fetchTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  // Add custom field
  const handleAddField = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/${activeTemplateId}/fields`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fieldName,
          fieldType,
          options:
          fieldType === "dropdown"
            ? dropdownOptions.map((opt) => ({ optionValue: opt }))
            : []
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setShowFieldForm(false);
      setFieldName("");
      setFieldType("text");
      setDropdownOptions([""]);
      fetchTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditField = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetch(`${API}/fields/${editingField.fieldId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fieldName,
          fieldType,
          options:
            fieldType === "dropdown"
              ? dropdownOptions.map((opt) => ({ optionValue: opt }))
              : []
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setShowFieldForm(false);
      setEditingField(null);
      setFieldName("");
      setFieldType("text");
      setDropdownOptions([""]);
      fetchTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete custom field
  const deleteField = async (fieldId) => {
    if (!window.confirm("Delete this custom field?")) return;

    try {
      const res = await fetch(`${API}/fields/${fieldId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      fetchTemplates();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="page-container">
      <div className="info-header">
        <div>
          <h3 style={{ color: '#2d5016', margin: 0 }}>
            {user?.fullName || `${user?.firstName} ${user?.lastName}` || user?.email}
          </h3>
          <h3 style={{ color: '#2d5016', margin: 0 }}>{user?.companyName}</h3>
          <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0 0' }}>
            {user?.email} - {user?.role}
          </p>
        </div>
        <button 
          onClick={logout}
          style={{
            background: '#ff8c00',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600'
          }}
        >
          Logout
        </button>
      </div>
      <div className="page-content">
      <div className="page-header">
        <div className="header-title-section">
          <h2>Product Templates</h2>
        </div>

        <div className="header-actions">
          <button
            className="button back-btn"
            onClick={() => {navigate('/products')}}
          >
            <ArrowLeft style={{ width: '16px', height: '16px' }} />
            Back To Products
          </button>

          <button
            className="button primary-btn"
            onClick={() => {
              setShowForm(true);
              setEditingTemplate(null);
              setTemplateName("");
            }}
          >
            + Create Template
          </button>
        </div>
      </div>

      {error && <p className="error-msg">{error}</p>}

      {loading ? (
        <p>Loading templates...</p>
      ) : (
        <table className="template-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Custom Fields</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.templateId}>
                <td>{t.templateName}</td>
                <td>
                  {t.customFields?.length ? (
                    t.customFields.map((f) => (
                      <span key={f.fieldId} className="field-chip">
                        {f.fieldName} ({f.fieldType})

                        {/*<button
                          className="chip-edit"
                          onClick={() => {
                            setActiveTemplateId(t.templateId);
                            setEditingField(f);
                            setFieldName(f.fieldName);
                            setFieldType(f.fieldType);
                            setDropdownOptions(
                              f.options?.length ? f.options.map(o => o.optionValue) : [""]
                            );
                            setShowFieldForm(true);
                          }}
                        >
                          <Edit style={{ width: '18px', height: '18px' }} />
                        </button>*/}
                        <button
                          className="chip-delete"
                          onClick={() => deleteField(f.fieldId)}
                        >
                          <X style={{ width: '18px', height: '18px' }} />
                        </button>
                      </span>
                    ))
                  ) : (
                    <i>No fields</i>
                  )}
                </td>

                <td>
                  <button
                    className="edit-btn"
                    onClick={() => {
                      setEditingTemplate(t);
                      setTemplateName(t.templateName);
                      setShowForm(true);
                    }}
                  >
                    Edit
                  </button>

                  <button
                    className="button add-field-btn"
                    onClick={() => {
                      setActiveTemplateId(t.templateId);
                      setEditingField(null);
                      setFieldName("");
                      setFieldType("text");
                      setDropdownOptions([""]);
                      setShowFieldForm(true);
                    }}
                  >
                    + Add Field
                  </button>

                  <button
                    className="button danger-btn small-btn"
                    onClick={() => deleteTemplate(t.templateId)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create / Edit Template Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingTemplate ? "Edit Template" : "Create Template"}</h3>

            <form onSubmit={handleSubmitTemplate}>
              <div className="form-group">
                <input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  required
                  placeholder=" "
                />
                <label>Name</label>
              </div>

              <div className="modal-actions">
                <button type="submit" className="button primary-btn">
                  Save
                </button>
                <button
                  className="button"
                  onClick={() => setShowForm(false)}
                  type="button"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Custom Field Modal */}
      {showFieldForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{editingField ? "Edit Custom Field" : "Add Custom Field"}</h3>

            <form onSubmit={editingField ? handleEditField : handleAddField}>
              <div className="form-group">
                <input
                  value={fieldName}
                  onChange={(e) => setFieldName(e.target.value)}
                  required
                  placeholder=" "
                />
                <label>Field Name</label>
              </div>

              <div className="form-group">
                <select
                  value={fieldType}
                  onChange={(e) => setFieldType(e.target.value)}
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="boolean">Boolean</option>
                </select>
                <label>Field Type</label>
              </div>

              {/* Dropdown options */}
              {fieldType === "dropdown" && (
                <div className="dropdown-options">
                  
                  <label><h4>Dropdown Options</h4></label>

                  {dropdownOptions.map((opt, i) => (
                    <div key={i} className="option-row">
                      <div className="form-group">
                        <input
                          type="text"
                          value={opt}
                          placeholder={`Option ${i + 1}`}
                          onChange={(e) => {
                            const updated = [...dropdownOptions];
                            updated[i] = e.target.value;
                            setDropdownOptions(updated);
                          }}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                      <button className="cancel-option-btn"
                        type="button"
                        onClick={() => {
                          const updated = dropdownOptions.filter((_, idx) => idx !== i);
                          setDropdownOptions(updated);
                        }}
                      >
                        X
                      </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="add-option-btn"
                    onClick={() => setDropdownOptions([...dropdownOptions, ""])}
                  >
                    + Add Option
                  </button>
                </div>
              )}

              <div className="modal-actions">
                <button type="submit" className="button primary-btn">
                  {editingField ? "Save" : "Add"}
                </button>
                <button
                  className="button"
                  type="button"
                  onClick={() => setShowFieldForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
